import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import {
  getAccessor,
  getType,
  getWidth,
  isSortable,
  getDefaultValue,
  getMethodName,
  getKeyName,
  getVariables
} from '@nile/client_management/src/Pebbles/InvoiceAuditContainer';
import { DateTimeCell } from '@nile/mangifera/src/Components/Ticket';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  ContentDeciderContainer,
  getTranslatedText,
  Loader,
  TableContainer,
  SkeletonTableLoading,
  useProduct
} from '@nile/components';
import { AccountClient, GET_OFFERED_ITEMS_LIST } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import EditableCell from './EditableCell';
import InvoiceAuditActionCell from './InvoiceAuditActionCell';
import TotalCell from './TotalCell';
import CurrencyCell from './CurrencyCell';
import ServiceItemCell from './ServiceItemCell';
import ProjectAndTicketCell from './ProjectAndTicketIdCell';
import ProductHourCell from './ProductHourCell';
import InvoiceAuditNotesCell from './InvoiceAuditNotesCell';

const ROW_COUNT = 20;

function InvoiceDataTable(props) {
  const { intl, match, defaultCondition, isTableEditable = true } = props;
  const { id, sectionId } = match.params;
  const viewId = sectionId || id;
  const loading = false;
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);
  const [data, setData] = useState([]);
  const dataRef = useRef(0);
  dataRef.current = data;
  const [listLoading, setListLoading] = useState(true);
  const [listInfo, setListInfo] = useState({});
  const ticketContext = useContext(TicketContext);
  const currentProduct = useProduct();

  useEffect(() => {
    fetchViewData();
  }, [defaultCondition]);

  const getActions = (col) => {
    switch (col.name) {
      case 'displayId':
        return {
          onClick: (item) => {
            const ticketId = item?.ticket?.displayId;
            const projectId = item?.project?.displayId;

            if (ticketId)
              ticketContext.openTab({
                module: 'TICKET',
                data: item.ticket
              });

            if (projectId) {
              ticketContext.openTab({
                module: 'PROJECT_DETAIL',
                data: item.project
              });
            }
          }
        };
      default:
        return;
    }
  };

  /**
   * Method to get Service items.
   * Call this method to fetch/refetch the data on mounting/sorting/filtering
   * @param {object} personalization
   * @param {object} pageOptions
   */
  const fetchViewData = async (personalization, pageOptions) => {
    setListLoading(true);
    const response = await AccountClient.query({
      query: GET_OFFERED_ITEMS_LIST,
      variables: {
        isBillingQueue: true,
        listInfo: pageOptions
          ? {
            ...pageOptions,
            condition: defaultCondition
          }
          : {
            page: 1,
            pageSize: ROW_COUNT,
            condition: defaultCondition
          }
      },
      fetchPolicy: 'network-only'
    });
    setListLoading(false);

    const cols = [
      {
        name: 'billDate',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.date'
        ),
        dataType: 'daterange'
      },
      {
        name: 'displayId',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.ticketId'
        )
      },
      {
        name: 'client',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.client'
        ),
        dataType: 'entity'
      },
      {
        name: 'notes',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.notes'
        )
      },
      {
        name: 'technician.name',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.technicianName'
        )
      },
      {
        name: 'serviceItem',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.serviceItem'
        )
      },
      {
        name: 'unitsOrHours',
        label: getTranslatedText(intl, 'attribute.quantity.unitsOrHours')
      },
      {
        name: 'unitPrice',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.unitPrice'
        )
      },
      {
        name: 'discountRate',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.discount'
        )
      },
      {
        name: 'amount',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.amount'
        )
      },
      {
        name: 'revisedQuantity',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.revisedQuantity'
        )
      },
      {
        name: 'revisedUnitPrice',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.revisedUnitPrice'
        )
      },
      {
        name: 'revisedDiscountRate',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.revisedDiscountRate'
        )
      },
      {
        name: 'revisedAmount',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.total'
        )
      }
    ];

    const iTcolToRemove = (col) => {
      if (currentProduct == 'IT') {
        return col.filter(
          (co) =>
            ![
              'client',
              'unitsOrHours',
              'unitPrice',
              'discountRate',
              'amount',
              'revisedQuantity',
              'revisedUnitPrice',
              'revisedDiscountRate',
              'revisedAmount'
            ].includes(co.name)
        );
      }
      return col;
    };

    const columns = iTcolToRemove(cols).map((col, idx) => {
      return {
        Header: col.label,
        accessor: getAccessor(col),
        width: getWidth(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !isSortable(col),
        disableFilters: true,
        helpers: {
          getDefaultValue,
          getMethodName,
          getKeyName,
          getVariables
        },
        field: col,
        wrap: col.name === 'notes',
        isBold: col.name === 'revisedAmount',
        alignRight: col.name === 'revisedAmount',
        view: viewId,
        dataType: col.name === 'billDate' ? 'date' : '',
        actions: getActions(col),
        isEditable: isTableEditable
      };
    });

    setColumnGroup([{ Header: 'Name', columns }]);
    setData(response?.data?.getOfferedItems?.items);
    setListInfo(response?.data?.getOfferedItems?.listInfo);
  };

  if (loading) {
    return (
      <div className="flex center jctr fw h-300">
        <Loader />
      </div>
    );
  }

  const defaultColumnCells = {
    Action: InvoiceAuditActionCell,
    Total: TotalCell,
    Currency: CurrencyCell,
    Edit: EditableCell,
    DateTime: DateTimeCell,
    ServiceItem: ServiceItemCell,
    TicketProjectId: ProjectAndTicketCell,
    ProductHour: ProductHourCell,
    Notes: InvoiceAuditNotesCell
  };

  const checkRowHighlight = (row) => {
    return row.original.operationStatus === 'PROGRESS';
  };

  const checkDisabledRow = (row) => {
    return row.original.operationStatus === 'PROGRESS';
  };

  return (
    <TableContainer>
      <ContentDeciderContainer
        isLoading={loading || !columnGroup[0]?.columns?.length}
        hasContent
        fullHeight
        mainContent={
          <TableView
            noBulkSelect
            context={{ viewConditions: defaultCondition }}
            columns={columnGroup}
            data={data}
            setData={setData}
            rowCount={ROW_COUNT}
            refreshTable={fetchViewData}
            listInfo={listInfo}
            pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
            pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
            recordCount={listInfo.totalCount}
            defaultColumnCells={defaultColumnCells}
            activePage={listInfo.page || 0}
            isLoading={listLoading}
            fullHeight
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/InvoiceAudit.svg`,
              heading: getTranslatedText(
                intl,
                'invoiceAudit.list.noData.heading'
              ),
              description: getTranslatedText(
                intl,
                'invoiceAudit.list.noData.description'
              )
            }}
            cardTable={!!sectionId}
            alignStart
            checkRowHighlight={checkRowHighlight}
            checkDisabledRow={checkDisabledRow}
          />
        }
        loadingContent={<SkeletonTableLoading />}
        notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
      />
    </TableContainer>
  );
}

export default injectIntl(withRouter(InvoiceDataTable));
